import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { FormattedMessage, useIntl } from 'react-intl';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import DateTime from '../../../utils/DateTime';
import RenewalStatus from './RenewalStatus';
import {
  PlanInterval,
  ProductType,
  SecondarySource,
  Subscription,
  SubscriptionMetadata,
  SubscriptionSource,
} from '../../../models';
import { BillingCycle } from './BillingCycle';

export const SubscriptionTableView = (
  { groupId, subscriptions }: {
    groupId: string,
    subscriptions: Subscription[]
  },
) => {
  const intl = useIntl();

  const renderTableHeader = () => (
    <TableHead sx={{ backgroundColor: '#F5F6FA' }}>
      <TableRow key={`${groupId}-header`} id={`${groupId}-header`}>
        <TableCell key={`${groupId}-header-product`} id={`${groupId}-header-product`} align="left">Product</TableCell>
        <TableCell key={`${groupId}-header-interval`} id={`${groupId}-header-interval`} align="center">
          Billing
          Cycle
        </TableCell>
        <TableCell key={`${groupId}-header-quota`} id={`${groupId}-header-quota`} align="center">Quantity</TableCell>
        <TableCell key={`${groupId}-header-expiry-date`} id={`${groupId}-header-expiry-date`} align="center">
          Expiry
          Date
        </TableCell>
        <TableCell key={`${groupId}-header-status`} id={`${groupId}-header-status`} align="left">
          Renewal
          Status
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderSubRow = (
    key: string,
    productName: string,
    billingInterval: PlanInterval,
    quantity: number,
    expiryDate: Date,
    canceledAt: Date | undefined,
    source: SubscriptionSource,
    secondarySource: SecondarySource,
    recurlySubscriptionId?: number,
  ) => (
    <TableRow
      key={`${groupId}-body-${key}`}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell
        id={`${groupId}-body-${key}-product-name`}
        align="left"
        width="25%"
      >
        <FormattedMessage id={`feature.${productName}.product.name`} />
      </TableCell>
      <TableCell
        id={`${groupId}-body-${key}-billing-interval`}
        align="center"
        width="10%"
      >
        <BillingCycle
          billingInterval={billingInterval}
          source={source}
          useChip
        />
      </TableCell>
      <TableCell
        id={`${groupId}-body-${key}-quantity`}
        align="center"
        width="10%"
      >
        {quantity}
      </TableCell>
      <TableCell
        id={`${groupId}-body-${key}-expiry-date`}
        align="center"
        width="15%"
      >
        {DateTime.formatDate(intl, expiryDate)}
      </TableCell>
      <TableCell
        id={`${groupId}-body-${key}-renewal-status`}
        align="left"
        width="25%"
      >
        <RenewalStatus
          productName={productName}
          source={source}
          secondarySource={secondarySource}
          canceledAt={canceledAt}
          expiryDate={expiryDate}
          recurlySubscriptionId={recurlySubscriptionId}
        />
      </TableCell>
    </TableRow>
  );

  const renderSubMetadataRow = (
    metadata: [string, SubscriptionMetadata][],
    subscription: Subscription,
  ) => metadata.map(([name, value], index) => {
    const { id, expiryDate, canceledAt } = subscription;
    return renderSubRow(
      `${id}-${index}`,
      name.toLowerCase(),
      subscription.billingInterval,
      Number(value),
      expiryDate,
      canceledAt,
      subscription.source,
      subscription.secondarySource,
      subscription.recurlySubscriptionId,
    );
  });

  const renderSubRowWithoutMetadata = (subscription: Subscription) => renderSubRow(
    `${subscription.id}`,
    subscription.feature.toLowerCase(),
    subscription.billingInterval,
    subscription.quota,
    subscription.expiryDate,
    subscription.canceledAt,
    subscription.source,
    subscription.secondarySource,
    subscription.recurlySubscriptionId,
  );

  const renderTableBody = () => (
    <TableBody>
      {subscriptions.map(subscription => {
        const metadata = subscription.metadata ? Object.entries(subscription.metadata) : [];

        if (subscription.productType === ProductType.TRUE_TEST) {
          return (
            <>
              {renderSubRowWithoutMetadata(subscription)}
              {renderSubMetadataRow(metadata, subscription)}
            </>
          );
        }

        if (metadata.length > 0) {
          return renderSubMetadataRow(metadata, subscription);
        }

        return renderSubRowWithoutMetadata(subscription);
      })}
    </TableBody>
  );

  return (
    <TableContainer component={Paper} sx={{ paddingTop: 2 }}>
      <Table sx={{ minWidth: 650 }} aria-label="subscription table">
        {/* Subscription table header */}
        {renderTableHeader()}

        {/* Subscription table body rows */}
        {renderTableBody()}
      </Table>
    </TableContainer>
  );
};
